import { Box, IconButton, Theme, Tooltip, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BoardDisplayMode, InheritanceDiagramNode } from './buildOrganizationInheritanceDiagram';
import {
  buildVisibleTreeGraph,
  DiagramLeafMode,
  layoutTreeGraph,
  PositionedTreeNode,
  TREE_CANVAS_PADDING,
  treeEdgePath,
} from './layoutOrganizationTree';

// Label for the leaf kind the active mode does not render (shown as "N … hidden").
const SUPPRESSED_LEAF_LABELS: Record<DiagramLeafMode, string> = {
  repos: 'member',
  members: 'repo',
  none: 'member/repo',
};

const MIN_SCALE = 0.35;
const MAX_SCALE = 1.8;
const WHEEL_ZOOM_STEP = 0.08;
const BUTTON_ZOOM_STEP = 0.1;

type Viewport = {
  scale: number;
  x: number;
  y: number;
};

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

function zoomViewportAtPoint(
  viewport: Viewport,
  pointerX: number,
  pointerY: number,
  nextScale: number,
): Viewport {
  const scale = clampScale(nextScale);
  const ratio = scale / viewport.scale;
  return {
    scale,
    x: pointerX - (pointerX - viewport.x) * ratio,
    y: pointerY - (pointerY - viewport.y) * ratio,
  };
}

function pointerInShell(shell: HTMLDivElement, clientX: number, clientY: number) {
  const rect = shell.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
}

const useStyles = makeStyles(theme => ({
  canvasShell: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.type === 'dark' ? '#111418' : '#f7f9fc',
    overflow: 'hidden',
    cursor: 'grab',
    userSelect: 'none',
  },
  canvasShellNativeFullscreen: {
    height: '100%',
    width: '100%',
    border: 'none',
    borderRadius: 0,
  },
  canvasShellDragging: {
    cursor: 'grabbing',
  },
  toolbar: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    display: 'flex',
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.25),
  },
  hint: {
    position: 'absolute',
    left: theme.spacing(1.5),
    bottom: theme.spacing(1.5),
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
  },
  svgViewport: {
    width: '100%',
    height: '100%',
  },
}));

function canvasHintFor(leafMode: DiagramLeafMode): string {
  if (leafMode === 'members') {
    return 'Drag to pan · scroll to zoom · expand teams to see members on the right';
  }
  if (leafMode === 'repos') {
    return 'Drag to pan · scroll to zoom · expand teams to see repos on the right';
  }
  return 'Drag to pan · scroll to zoom · switch to Members or Repos to drill down';
}

function boardModeLabel(mode: BoardDisplayMode): string {
  switch (mode) {
    case 'declared':
      return 'Differs from parent';
    case 'inherited':
      return 'Inherited';
    case 'override':
      return 'Override';
    default:
      return 'No board';
  }
}

function kindLabel(node: InheritanceDiagramNode): string {
  if (node.nodeKind === 'component') {
    return 'repo';
  }
  if (node.nodeKind === 'member') {
    return 'member';
  }
  if (node.nodeKind === 'bucket') {
    return 'bucket';
  }
  return node.groupType ?? 'group';
}

function groupTypeAccent(
  groupType: string | undefined,
  theme: Theme,
): string {
  switch (groupType) {
    case 'organization':
      return theme.palette.secondary.main;
    case 'department':
      return theme.palette.info.main;
    case 'team':
      return theme.palette.primary.main;
    default:
      return theme.palette.text.secondary;
  }
}

function repoNodeColors(
  mode: BoardDisplayMode,
  theme: Theme,
): { fill: string; stroke: string; accent: string; text: string } {
  if (mode === 'override') {
    return {
      fill: theme.palette.type === 'dark' ? '#3d3420' : '#fff8e8',
      stroke: theme.palette.warning.main,
      accent: theme.palette.warning.dark,
      text: theme.palette.text.primary,
    };
  }
  return {
    fill: theme.palette.type === 'dark' ? '#1a1f24' : '#eef1f5',
    stroke: theme.palette.type === 'dark' ? '#3a424d' : '#c5ced8',
    accent: theme.palette.text.secondary,
    text: theme.palette.text.primary,
  };
}

function nodeColors(
  mode: BoardDisplayMode,
  theme: Theme,
): { fill: string; stroke: string; accent: string; text: string } {
  switch (mode) {
    case 'declared':
      return {
        fill: theme.palette.primary.main,
        stroke: theme.palette.primary.dark,
        accent: theme.palette.primary.contrastText,
        text: theme.palette.primary.contrastText,
      };
    case 'override':
      return {
        fill: theme.palette.warning.main,
        stroke: theme.palette.warning.dark,
        accent: theme.palette.warning.contrastText,
        text: theme.palette.warning.contrastText,
      };
    case 'inherited':
      return {
        fill:
          theme.palette.type === 'dark'
            ? 'rgba(92, 124, 250, 0.22)'
            : 'rgba(25, 118, 210, 0.14)',
        stroke: theme.palette.primary.main,
        accent: theme.palette.primary.main,
        text: theme.palette.text.primary,
      };
    default:
      return {
        fill: theme.palette.background.paper,
        stroke: theme.palette.divider,
        accent: theme.palette.text.secondary,
        text: theme.palette.text.primary,
      };
  }
}

function PersonGlyph({
  x,
  y,
  size,
  fill,
}: {
  x: number;
  y: number;
  size: number;
  fill: string;
}) {
  return (
    <g transform={`translate(${x}, ${y})`} pointerEvents="none">
      <circle cx={size / 2} cy={size * 0.34} r={size * 0.18} fill="none" stroke={fill} strokeWidth={1.2} />
      <path
        d={`M ${size * 0.2} ${size * 0.88} C ${size * 0.24} ${size * 0.58}, ${size * 0.76} ${size * 0.58}, ${size * 0.8} ${size * 0.88}`}
        fill="none"
        stroke={fill}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    </g>
  );
}

function memberNodeColors(
  role: 'manager' | 'member' | undefined,
  theme: Theme,
): { fill: string; stroke: string; accent: string; text: string; badge: string } {
  if (role === 'manager') {
    return {
      fill: theme.palette.type === 'dark' ? '#2a2418' : '#fff8e6',
      stroke: theme.palette.type === 'dark' ? '#b8860b' : '#d4a017',
      accent: theme.palette.type === 'dark' ? '#e6b422' : '#9a7209',
      text: theme.palette.text.primary,
      badge: theme.palette.type === 'dark' ? '#e6b422' : '#9a7209',
    };
  }

  return {
    fill: theme.palette.type === 'dark' ? '#182028' : '#eef6f2',
    stroke: theme.palette.type === 'dark' ? '#3d5a4f' : '#9cb8ab',
    accent: theme.palette.type === 'dark' ? '#8fae9f' : '#4f6f62',
    text: theme.palette.text.primary,
    badge: theme.palette.type === 'dark' ? '#8fae9f' : '#4f6f62',
  };
}
function RepoGlyph({
  x,
  y,
  size,
  fill,
}: {
  x: number;
  y: number;
  size: number;
  fill: string;
}) {
  return (
    <g transform={`translate(${x}, ${y})`} pointerEvents="none">
      <rect x={0} y={1} width={size} height={size - 2} rx={2} fill="none" stroke={fill} strokeWidth={1.2} />
      <path
        d={`M ${size * 0.28} ${size * 0.38} L ${size * 0.5} ${size * 0.24} L ${size * 0.72} ${size * 0.38}`}
        fill="none"
        stroke={fill}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1={size * 0.5}
        y1={size * 0.24}
        x2={size * 0.5}
        y2={size * 0.78}
        stroke={fill}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    </g>
  );
}

function TreeNodeCard({
  node,
  onToggle,
  onCatalogNavigate,
  hasIncoming,
  hasOutgoing,
  leafMode,
  shadowFilterId,
}: {
  node: PositionedTreeNode;
  onToggle: (id: string) => void;
  onCatalogNavigate: (path: string) => void;
  hasIncoming: boolean;
  hasOutgoing: boolean;
  leafMode: DiagramLeafMode;
  shadowFilterId: string;
}) {
  const theme = useTheme();
  const data = node.data;
  const isRepo = data.nodeKind === 'component';
  const isMember = data.nodeKind === 'member';
  const { width, height } = node;
  let colors;
  if (isRepo) {
    colors = repoNodeColors(data.boardMode, theme);
  } else if (isMember) {
    colors = memberNodeColors(data.memberRole, theme);
  } else {
    colors = nodeColors(data.boardMode, theme);
  }
  const expandable = node.expandable;
  const boardText =
    data.projectKeys.length > 0
      ? data.projectKeys.join(', ')
      : boardModeLabel(data.boardMode);
  const inheritedHint =
    data.boardMode === 'inherited' && data.inheritedFromLabel
      ? `Inherited from ${data.inheritedFromLabel}`
      : '';
  let groupBoardLine = boardText;
  if (data.boardMode === 'declared') {
    groupBoardLine = `${boardText} · Differs from parent`;
  } else if (data.boardMode === 'inherited' && inheritedHint) {
    groupBoardLine = `${boardText} · ${inheritedHint}`;
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (expandable) {
      onToggle(node.id);
    }
  };

  const goPointerOrigin = useRef<{ x: number; y: number } | null>(null);

  const startCatalogPointer = (event: React.PointerEvent) => {
    event.stopPropagation();
    goPointerOrigin.current = { x: event.clientX, y: event.clientY };
  };

  const openCatalog = (event: React.PointerEvent) => {
    event.stopPropagation();
    const origin = goPointerOrigin.current;
    goPointerOrigin.current = null;
    if (!data.catalogPath || !origin) {
      return;
    }
    const moved =
      Math.abs(event.clientX - origin.x) > 4 || Math.abs(event.clientY - origin.y) > 4;
    if (moved) {
      return;
    }
    onCatalogNavigate(data.catalogPath);
  };

  const hiddenMetaParts: string[] = [];
  if (node.hasHiddenChildren) {
    hiddenMetaParts.push(`+${node.hiddenChildCount} hidden`);
  }
  if (node.hiddenLeafCount > 0) {
    const leafLabel = SUPPRESSED_LEAF_LABELS[leafMode];
    hiddenMetaParts.push(
      `${node.hiddenLeafCount} ${leafLabel}${node.hiddenLeafCount === 1 ? '' : 's'} hidden`,
    );
  }

  if (isMember) {
    const paddingX = 10;
    const iconSize = 12;
    const goWidth = 28;
    const goHeight = 18;
    const goMargin = 8;
    const hasGo = Boolean(data.catalogPath);
    const goX = width - goWidth - goMargin;
    const goY = height - goHeight - 8;
    const textLeft = paddingX + iconSize + 8;
    const nameAreaWidth = width - textLeft - paddingX;
    const roleLabel = data.memberRole === 'manager' ? 'Manager' : 'Member';
    const memberColors = memberNodeColors(data.memberRole, theme);
    const cornerRadius = 10;
    const portStroke =
      theme.palette.type === 'dark' ? '#6b7785' : '#98a5b3';
    const nameClipId = `member-name-${sanitizeSvgId(node.id)}`;

    return (
      <g transform={`translate(${node.x}, ${node.y})`} filter={`url(#${shadowFilterId})`}>
        <defs>
          <clipPath id={nameClipId}>
            <rect x={textLeft} y={0} width={Math.max(0, nameAreaWidth)} height={30} />
          </clipPath>
        </defs>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          rx={cornerRadius}
          ry={cornerRadius}
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth={data.memberRole === 'manager' ? 2 : 1.25}
          style={{ cursor: 'default' }}
          onPointerDown={event => event.stopPropagation()}
        />
        <PersonGlyph x={paddingX} y={12} size={iconSize} fill={colors.accent} />
        <text
          x={textLeft}
          y={22}
          fill={colors.text}
          fontSize={12}
          fontWeight={600}
          pointerEvents="none"
          clipPath={`url(#${nameClipId})`}
        >
          {data.label}
        </text>
        <text
          x={textLeft}
          y={42}
          fill={memberColors.badge}
          fontSize={10}
          fontWeight={700}
          pointerEvents="none"
        >
          {roleLabel}
        </text>
        {hasIncoming ? (
          <NodePort x={0} y={height / 2} fill={colors.fill} stroke={portStroke} />
        ) : null}
        {hasGo ? (
          <GoButton
            x={goX}
            y={goY}
            width={goWidth}
            height={goHeight}
            theme={theme}
            onPointerDown={startCatalogPointer}
            onPointerUp={openCatalog}
          />
        ) : null}
      </g>
    );
  }

  if (isRepo) {
    const paddingX = 10;
    const iconSize = 12;
    const goWidth = 28;
    const goHeight = 18;
    const goMargin = 8;
    const hasGo = Boolean(data.catalogPath);
    const goX = width - goWidth - goMargin;
    const goY = 8;
    const textLeft = paddingX + iconSize + 8;
    const titleAreaWidth = hasGo ? goX - textLeft - 6 : width - textLeft - paddingX;
    const metaAreaWidth = width - textLeft - paddingX;
    const nameMaxChars = maxCharsForWidth(titleAreaWidth, 6.5);
    const metaMaxChars = maxCharsForWidth(metaAreaWidth, 5.5);
    const cornerRadius = 10;
    const metaText = inheritedHint ? `${boardText} · ${inheritedHint}` : boardText;
    const portStroke =
      theme.palette.type === 'dark' ? '#6b7785' : '#98a5b3';
    const clipId = `repo-title-${sanitizeSvgId(node.id)}`;

    return (
      <g transform={`translate(${node.x}, ${node.y})`} filter={`url(#${shadowFilterId})`}>
        <defs>
          <clipPath id={clipId}>
            <rect x={textLeft} y={0} width={Math.max(0, titleAreaWidth)} height={goY + goHeight} />
          </clipPath>
        </defs>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          rx={cornerRadius}
          ry={cornerRadius}
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth={data.boardMode === 'override' ? 1.8 : 1.25}
          strokeDasharray={data.boardMode === 'inherited' ? '4 3' : undefined}
          style={{ cursor: 'default' }}
          onPointerDown={event => event.stopPropagation()}
        />
        <RepoGlyph x={paddingX} y={11} size={iconSize} fill={colors.accent} />
        <text
          x={textLeft}
          y={22}
          fill={colors.text}
          fontSize={11}
          fontWeight={600}
          fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          pointerEvents="none"
          clipPath={`url(#${clipId})`}
        >
          {truncate(data.label, nameMaxChars)}
        </text>
        <text
          x={textLeft}
          y={44}
          fill={colors.accent}
          fontSize={9}
          fontWeight={500}
          pointerEvents="none"
        >
          {truncate(metaText, metaMaxChars)}
        </text>
        {hasIncoming ? (
          <NodePort x={0} y={height / 2} fill={colors.fill} stroke={portStroke} />
        ) : null}
        {hasGo ? (
          <GoButton
            x={goX}
            y={goY}
            width={goWidth}
            height={goHeight}
            theme={theme}
            onPointerDown={startCatalogPointer}
            onPointerUp={openCatalog}
          />
        ) : null}
      </g>
    );
  }

  const titleSize = 14;
  const metaSize = 11;
  const boardSize = 11;
  const cornerRadius = 12;
  const paddingX = 14;
  const goWidth = 28;
  const goHeight = 20;
  const goMargin = 10;
  const hasGo = Boolean(data.catalogPath);
  const goX = width - goWidth - goMargin;
  const goY = 8;
  const titleAreaWidth = hasGo ? goX - paddingX - 6 : width - paddingX * 2;
  const bodyAreaWidth = width - paddingX * 2;
  const titleMax = maxCharsForWidth(titleAreaWidth, 7.5);
  const metaMax = maxCharsForWidth(bodyAreaWidth, 6.2);
  const boardMax = maxCharsForWidth(bodyAreaWidth, 5.8);
  const accentColor = groupTypeAccent(data.groupType, theme);
  let portStroke = theme.palette.divider;
  if (data.boardMode === 'inherited') {
    portStroke = theme.palette.primary.main;
  } else if (data.boardMode === 'declared') {
    portStroke = theme.palette.primary.dark;
  }
  const groupMeta = [
    kindLabel(data),
    data.managerLabel ? `Mgr: ${data.managerLabel}` : '',
    node.isExpanded && expandable ? 'expanded' : '',
    ...hiddenMetaParts,
  ]
    .filter(Boolean)
    .join(' · ');
  const titleClipId = `group-title-${sanitizeSvgId(node.id)}`;

  return (
    <g transform={`translate(${node.x}, ${node.y})`} filter={`url(#${shadowFilterId})`}>
      <defs>
        <clipPath id={titleClipId}>
          <rect x={paddingX} y={0} width={Math.max(0, titleAreaWidth)} height={goY + goHeight + 2} />
        </clipPath>
      </defs>
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={cornerRadius}
        ry={cornerRadius}
        fill={colors.fill}
        stroke={colors.stroke}
        strokeWidth={data.boardMode === 'inherited' ? 2 : 1.75}
        style={{ cursor: expandable ? 'pointer' : 'default' }}
        onClick={handleClick}
        onPointerDown={event => event.stopPropagation()}
      />
      <path
        d={`M ${cornerRadius} 0 H ${width - cornerRadius} Q ${width} 0 ${width} ${cornerRadius} V 24 H 0 V ${cornerRadius} Q 0 0 ${cornerRadius} 0 Z`}
        fill={accentColor}
        opacity={0.14}
        pointerEvents="none"
      />
      <rect
        x={0}
        y={8}
        width={4}
        height={height - 16}
        rx={2}
        fill={accentColor}
        pointerEvents="none"
      />
      <text
        x={paddingX}
        y={24}
        fill={colors.text}
        fontSize={titleSize}
        fontWeight={700}
        pointerEvents="none"
        clipPath={`url(#${titleClipId})`}
      >
        {truncate(data.label, titleMax)}
      </text>
      <text
        x={paddingX}
        y={42}
        fill={colors.text}
        fontSize={metaSize}
        opacity={0.85}
        pointerEvents="none"
      >
        {truncate(groupMeta, metaMax)}
      </text>
      <text
        x={paddingX}
        y={60}
        fill={colors.text}
        fontSize={boardSize}
        fontWeight={600}
        pointerEvents="none"
      >
        {truncate(groupBoardLine, boardMax)}
      </text>
      {hasIncoming ? (
        <NodePort x={0} y={height / 2} fill={colors.fill} stroke={portStroke} />
      ) : null}
      {hasOutgoing ? (
        <NodePort x={width} y={height / 2} fill={colors.fill} stroke={portStroke} />
      ) : null}
      {hasGo ? (
        <GoButton
          x={goX}
          y={goY}
          width={goWidth}
          height={goHeight}
          theme={theme}
          onPointerDown={startCatalogPointer}
          onPointerUp={openCatalog}
        />
      ) : null}
    </g>
  );
}

function truncate(value: string, max: number): string {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max - 1)}…`;
}

function sanitizeSvgId(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function maxCharsForWidth(widthPx: number, charWidthPx: number): number {
  return Math.max(4, Math.floor(widthPx / charWidthPx));
}

function GoButton({
  x,
  y,
  width,
  height,
  theme,
  onPointerDown,
  onPointerUp,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  theme: Theme;
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
}) {
  return (
    <g style={{ cursor: 'pointer' }} onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={5}
        fill={theme.palette.background.paper}
        stroke={theme.palette.divider}
        strokeWidth={1}
      />
      <text
        x={x + width / 2}
        y={y + height - 5}
        textAnchor="middle"
        fill={theme.palette.text.secondary}
        fontSize={9}
        fontWeight={700}
        pointerEvents="none"
      >
        GO
      </text>
    </g>
  );
}

function NodePort({
  x,
  y,
  fill,
  stroke,
}: {
  x: number;
  y: number;
  fill: string;
  stroke: string;
}) {
  return (
    <circle
      cx={x}
      cy={y}
      r={3.5}
      fill={fill}
      stroke={stroke}
      strokeWidth={1.5}
      pointerEvents="none"
    />
  );
}

function TreeEdge({
  edge,
  stroke,
  strokeWidth,
  dashArray,
}: {
  edge: {
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
  };
  stroke: string;
  strokeWidth: number;
  dashArray?: string;
}) {
  return (
    <path
      d={treeEdgePath(edge.sourceX, edge.sourceY, edge.targetX, edge.targetY)}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={dashArray}
    />
  );
}

export function OrganizationInheritanceTreeCanvas({
  root,
  expandedIds,
  onToggle,
  onCatalogNavigate,
  canvasHeight = 520,
  leafMode = 'members',
}: {
  root: InheritanceDiagramNode;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onCatalogNavigate?: (path: string) => void;
  canvasHeight?: number | string;
  leafMode?: DiagramLeafMode;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  // Unique per canvas instance so the two canvases (official + other owners)
  // rendered together do not collide on the SVG filter id.
  const shadowFilterId = `org-tree-node-shadow-${sanitizeSvgId(useId())}`;
  const handleCatalogNavigate = useCallback(
    (path: string) => {
      if (onCatalogNavigate) {
        onCatalogNavigate(path);
        return;
      }
      navigate(path);
    },
    [navigate, onCatalogNavigate],
  );
  const shellRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Viewport>({ scale: 0.85, x: 40, y: 24 });
  const [dragging, setDragging] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const dragOrigin = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const layout = useMemo(() => {
    const graph = buildVisibleTreeGraph(root, expandedIds, leafMode);
    return layoutTreeGraph(graph);
  }, [root, expandedIds, leafMode]);

  const connectionFlags = useMemo(() => {
    const incoming = new Set<string>();
    const outgoing = new Set<string>();
    for (const edge of layout.edges) {
      outgoing.add(edge.from);
      incoming.add(edge.to);
    }
    return { incoming, outgoing };
  }, [layout.edges]);

  const edgeStrokeFor = useCallback(
    (targetKind: InheritanceDiagramNode['nodeKind']) => {
      if (targetKind === 'component') {
        return theme.palette.type === 'dark' ? '#5f6d7a' : '#a8b4c0';
      }
      if (targetKind === 'member') {
        return theme.palette.type === 'dark' ? '#b8860b' : '#c99200';
      }
      return theme.palette.type === 'dark' ? '#5c7cfa' : '#6b8cce';
    },
    [theme.palette.type],
  );

  const fitToView = useCallback(() => {
    const shell = shellRef.current;
    if (!shell || layout.width === 0) {
      return;
    }

    const availableWidth = shell.clientWidth - TREE_CANVAS_PADDING * 2;
    const availableHeight = shell.clientHeight - TREE_CANVAS_PADDING * 2;
    const nextScale = clampScale(
      Math.min(availableWidth / layout.width, availableHeight / layout.height),
    );
    setViewport({
      scale: nextScale,
      x: TREE_CANVAS_PADDING,
      y: Math.max(
        TREE_CANVAS_PADDING,
        (shell.clientHeight - layout.height * nextScale) / 2,
      ),
    });
  }, [layout.height, layout.width]);

  const zoomAtClientPoint = useCallback((clientX: number, clientY: number, deltaScale: number) => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }
    const pointer = pointerInShell(shell, clientX, clientY);
    setViewport(current =>
      zoomViewportAtPoint(current, pointer.x, pointer.y, current.scale + deltaScale),
    );
  }, []);

  const zoomAtShellCenter = useCallback((deltaScale: number) => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }
    setViewport(current =>
      zoomViewportAtPoint(
        current,
        shell.clientWidth / 2,
        shell.clientHeight / 2,
        current.scale + deltaScale,
      ),
    );
  }, []);

  useEffect(() => {
    fitToView();
    // Only reset the viewport when switching trees, not when expanding nodes.
  }, [root.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fitToView();
  }, [canvasHeight, isNativeFullscreen, leafMode, fitToView]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsNativeFullscreen(document.fullscreenElement === shellRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleNativeFullscreen = async () => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }

    try {
      if (document.fullscreenElement === shell) {
        await document.exitFullscreen();
      } else {
        await shell.requestFullscreen();
      }
    } catch {
      // Browser blocked fullscreen — ignore.
    }
  };

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) {
      return undefined;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const delta = event.deltaY > 0 ? -WHEEL_ZOOM_STEP : WHEEL_ZOOM_STEP;
      zoomAtClientPoint(event.clientX, event.clientY, delta);
    };

    shell.addEventListener('wheel', handleWheel, { passive: false });
    return () => shell.removeEventListener('wheel', handleWheel);
  }, [zoomAtClientPoint]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    dragOrigin.current = {
      x: event.clientX,
      y: event.clientY,
      tx: viewport.x,
      ty: viewport.y,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOrigin.current;
    if (!origin) {
      return;
    }
    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    setViewport(current => ({
      ...current,
      x: origin.tx + dx,
      y: origin.ty + dy,
    }));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) {
      return;
    }
    dragOrigin.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      ref={shellRef}
      className={`${classes.canvasShell} ${
        dragging ? classes.canvasShellDragging : ''
      } ${isNativeFullscreen ? classes.canvasShellNativeFullscreen : ''}`}
      style={isNativeFullscreen ? undefined : { height: canvasHeight, minHeight: 360 }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <Box className={classes.toolbar} onPointerDown={event => event.stopPropagation()}>
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={() => zoomAtShellCenter(BUTTON_ZOOM_STEP)}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={() => zoomAtShellCenter(-BUTTON_ZOOM_STEP)}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to view">
          <IconButton size="small" onClick={fitToView}>
            <CenterFocusStrongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isNativeFullscreen ? 'Exit fullscreen' : 'Fullscreen graph'}>
          <IconButton size="small" onClick={toggleNativeFullscreen}>
            {isNativeFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="caption" className={classes.hint}>
        {canvasHintFor(leafMode)}
      </Typography>

      <svg className={classes.svgViewport}>
        <defs>
          <filter id={shadowFilterId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.12" />
          </filter>
        </defs>
        <g transform={`translate(${viewport.x}, ${viewport.y}) scale(${viewport.scale})`}>
          {layout.edges.map(edge => {
            const targetNode = layout.nodes.find(node => node.id === edge.to);
            const isNonManagerMemberEdge =
              edge.targetKind === 'member' && targetNode?.data.memberRole !== 'manager';
            let stroke = edgeStrokeFor(edge.targetKind);
            if (isNonManagerMemberEdge) {
              stroke = theme.palette.type === 'dark' ? '#4f6f62' : '#8aa89a';
            }
            const isLeafEdge = edge.targetKind === 'component' || edge.targetKind === 'member';
            return (
              <TreeEdge
                key={`${edge.from}-${edge.to}`}
                edge={edge}
                stroke={stroke}
                strokeWidth={isLeafEdge ? 1.5 : 2}
                dashArray={isLeafEdge ? '5 4' : undefined}
              />
            );
          })}
          {layout.nodes.map(node => (
            <TreeNodeCard
              key={node.id}
              node={node}
              onToggle={onToggle}
              onCatalogNavigate={handleCatalogNavigate}
              hasIncoming={connectionFlags.incoming.has(node.id)}
              hasOutgoing={connectionFlags.outgoing.has(node.id)}
              leafMode={leafMode}
              shadowFilterId={shadowFilterId}
            />
          ))}
        </g>
      </svg>
    </div>
  );
}
